import {
  type EdgeData,
  type EdgeId,
  type EdgeRecord,
  type NodeData,
  type NodeId,
  type NodeRecord,
  getEdgeBoxId,
  isRenderableEdgeData,
} from '@ipfabric/utils';
import { keepIdentityIfEqual } from '@ipfabric/utils/fp/keepIdentityIfEqual';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import {
  createGraphEdgeDisplayObject,
  getEdgeBounds,
  updateGraphEdgeDisplayObject,
} from 'components/Graph/GraphApp/GraphEdge';
import {
  createGraphNodeDisplayObject,
  getNodeBounds,
  updateGraphNodeDisplayObject,
} from 'components/Graph/GraphApp/GraphNode';
import type {
  EdgeMouseEvent,
  NodeMouseEvent,
} from 'components/Graph/GraphApp/events/GraphAppEvents';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { objectDiff } from 'utils/object/objectDiff';
import { boundsEq } from 'utils/pixi/Bounds';
import { refreshEdgeVisibility } from '../helpers/refreshEdgeVisibility';

const optionBoundsEq = option.getEq(boundsEq);
const keepOptionBoundsIdentityIfEqual = keepIdentityIfEqual(optionBoundsEq);

export const renderData: GraphRendererPlugin = (pluginInterface) => {
  const addEdge = (id: EdgeId, edgeData: EdgeData) => {
    const edge = createGraphEdgeDisplayObject(id, edgeData, pluginInterface);
    pluginInterface.graphLayer.addChild(edge);
    pluginInterface.edgeDisplayObjects.set(id, edge);
    edge.on('mousedown', (event: EdgeMouseEvent) => {
      pluginInterface.events.emit('edgeMouseDown', event);
    });
    edge.on('mouseover', () => pluginInterface.onEdgeMouseOver(id));
    edge.on('mouseout', () => pluginInterface.onEdgeMouseOut(id));

    if (pluginInterface.getGraphData().hiddenNodes.has(edgeData.source)) {
      edge.reasonsToHide.add('SOURCE_NODE_HIDDEN');
    }
    if (pluginInterface.getGraphData().hiddenNodes.has(edgeData.target)) {
      edge.reasonsToHide.add('TARGET_NODE_HIDDEN');
    }

    const bounds = getEdgeBounds(edgeData, edge);
    pluginInterface.events.emit('edgeAdded', {
      bounds,
      edge,
      edgeData,
      id,
    });
    pluginInterface.events.emit('edgeBoundsChange', { edge, edgeData, bounds });
  };

  const addNode = (id: NodeId, nodeData: NodeData) => {
    const node = createGraphNodeDisplayObject(id, nodeData, pluginInterface);
    pluginInterface.graphLayer.addChild(node);
    pluginInterface.nodeDisplayObjects.set(id, node);
    node.on('mousedown', (event: NodeMouseEvent) => {
      pluginInterface.events.emit('nodeMouseDown', event);
    });
    const bounds = getNodeBounds(nodeData, node);
    pluginInterface.events.emit('nodeAdded', {
      bounds,
      id,
      node,
      nodeData,
    });
    pluginInterface.events.emit('nodeBoundsChange', { node, bounds });
  };

  const updateEdge = (
    id: EdgeId,
    edgeData: EdgeData,
    oldEdgeData: EdgeData,
    oldNodes: NodeRecord = {},
  ) => {
    const edge = pluginInterface.edgeDisplayObjects.get(id);
    const { nodes, hiddenNodes } = pluginInterface.getGraphData();
    if (edge) {
      const oldBounds = getEdgeBounds(oldEdgeData, edge);
      if (isRenderableEdgeData(edgeData)) {
        // not hidden using hideEdge because affected bounds will be updated here after positions are updated below
        edge.reasonsToHide.delete('NOT_RENDERABLE');
        updateGraphEdgeDisplayObject(edge, edgeData);
      } else {
        edge.reasonsToHide.add('NOT_RENDERABLE');
      }
      const newBounds = getEdgeBounds(edgeData, edge);
      // Edge doesn't hold information to which box it belongs so we have to
      // check it manually and force trigger bounds change. Happens when group by is changed and
      // nodes preserve locations.
      const boxChanged =
        getEdgeBoxId(edgeData, nodes) !== getEdgeBoxId(oldEdgeData, oldNodes);
      if (!optionBoundsEq.equals(oldBounds, newBounds) || boxChanged) {
        pluginInterface.events.emit('edgeBoundsChange', {
          edge,
          edgeData,
          bounds: newBounds,
        });
      }

      refreshEdgeVisibility(edge, edgeData, hiddenNodes);
    }
  };

  const updateNode = (
    id: NodeId,
    nodeData: NodeData,
    oldNodeData: NodeData,
  ) => {
    const node = pluginInterface.nodeDisplayObjects.get(id);
    if (node) {
      const oldBounds = getNodeBounds(oldNodeData, node);
      updateGraphNodeDisplayObject(
        node,
        nodeData,
        oldNodeData,
        pluginInterface,
      );
      // keep object identity so that bounds updates should be
      // easily detected using oldBounds !== newBounds
      const bounds = keepOptionBoundsIdentityIfEqual(oldBounds)(
        getNodeBounds(nodeData, node),
      );
      pluginInterface.events.emit('nodeChange', {
        id,
        bounds,
        node,
        nodeData,
        oldNodeData,
        oldBounds,
      });
      if (bounds !== oldBounds) {
        // emitted as special event since many features care only if the node moved
        pluginInterface.events.emit('nodeBoundsChange', { node, bounds });
      }
    }
  };

  const removeEdge = (id: EdgeId, edgeData: EdgeData) => {
    pipe(
      pluginInterface.edgeDisplayObjects,
      plainMap.popMutable(id),
      option.map((edge) => {
        pluginInterface.events.emit('beforeRemoveEdge', { id, edge, edgeData });
        pluginInterface.events.emit('edgeBoundsChange', {
          edge,
          edgeData,
          bounds: option.none,
        });
        edge.destroy();
      }),
    );
  };

  const removeNode = (id: NodeId, nodeData: NodeData) => {
    pipe(
      pluginInterface.nodeDisplayObjects,
      plainMap.popMutable(id),
      option.map((node) => {
        pluginInterface.events.emit('beforeRemoveNode', { id, node, nodeData });
        pluginInterface.events.emit('nodeBoundsChange', {
          node,
          bounds: option.none,
        });
        node.destroy();
      }),
    );
  };

  const updateEdges = (
    prevEdges: EdgeRecord = {},
    prevNodes: NodeRecord = {},
  ): void => {
    const { edges } = pluginInterface.getGraphData();
    if (edges === prevEdges) {
      return;
    }
    const diff = objectDiff<EdgeRecord, EdgeId>(prevEdges, edges);
    for (const id of diff.removed) {
      removeEdge(id, prevEdges[id]!);
    }
    for (const id of diff.modified) {
      updateEdge(id, edges[id]!, prevEdges[id]!, prevNodes);
    }
    for (const id of diff.added) {
      addEdge(id, edges[id]!);
    }
  };

  const updateNodes = (prevNodes: NodeRecord = {}): void => {
    const { nodes } = pluginInterface.getGraphData();
    if (nodes === prevNodes) {
      return;
    }
    const diff = objectDiff<NodeRecord, NodeId>(prevNodes, nodes);
    for (const id of diff.removed) {
      removeNode(id, prevNodes[id]!);
    }
    for (const id of diff.modified) {
      updateNode(id, nodes[id]!, prevNodes[id]!);
    }
    for (const id of diff.added) {
      addNode(id, nodes[id]!);
    }
  };

  pluginInterface.events.addListener('graphDataChange', (event) => {
    updateEdges(event.prevData?.edges, event.prevData?.nodes);
    updateNodes(event.prevData?.nodes);
    pluginInterface.events.emit('graphDataRendered', event);
  });
};
